import type { VclControlId } from './types.js';
import { VCL_CONTROL_IDS } from './types.js';
import { VCL_BASELINE_STEPS } from './baseline.js';

export interface VclControlDefinition {
  readonly id: VclControlId;
  readonly meaning: string;
  /** HSS / plan §8.1 must-nots - invariant over aesthetics. */
  readonly mustNot: readonly string[];
  readonly scope: string;
  /** Live file owner path for promotion dry-run / DG handoff (not auto-write). */
  readonly promotionTarget: string;
  readonly min: number;
  readonly max: number;
  readonly baseline: number;
  /** Human label for DEV panel. */
  readonly label: string;
  /** Unit description for slider/step UI. */
  readonly stepUnit: string;
}

/**
 * Exactly C1-C10. Order matches `VCL_CONTROL_IDS`.
 * Ranges are integer steps around frozen live baseline (`0`).
 */
export const VCL_CONTROLS: readonly VclControlDefinition[] = [
  {
    id: 'C1',
    label: 'Typography hierarchy',
    meaning:
      'Per-role size/weight/line/tracking for body | body-sm | label | button | h3 | h4 (CSS set).',
    mustNot: [
      'Change normative role meaning',
      'Apply a universal type size/tracking/leading multiplier across roles',
      'Emit title|caption roles (dropped; map was title->h3, caption->body-sm)',
    ],
    scope: 'Customer type roles bound to --vcl-type-{role}-*',
    promotionTarget: 'rpapp-customer/src/styles/customer.extensions.css',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C1,
    stepUnit: 'role size step (±1 ≈ 0.0625rem per role, independent)',
  },
  {
    id: 'C2',
    label: 'Vertical density',
    meaning: 'Semantic vertical stack spacing (xs…lg), not global margin.',
    mustNot: [
      'Crush touch geometry',
      'Crush safe clearances / structural spacing invariants',
      'Apply global * margin/padding multipliers',
    ],
    scope: 'Semantic stacks -> --vcl-space-stack-*',
    promotionTarget: 'rpapp-customer/src/styles/customer.extensions.css',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C2,
    stepUnit: 'stack scale step',
  },
  {
    id: 'C3',
    label: 'Horizontal density',
    meaning: 'Semantic horizontal gap/padding for rows, chips, trailing clusters.',
    mustNot: [
      'Crush readability',
      'Crush icons or trailing actions',
      'Drop below min interactive geometry',
    ],
    scope: 'Row/chip gaps -> --vcl-gap-inline-*',
    promotionTarget: 'rpapp-customer/src/styles/customer.extensions.css',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C3,
    stepUnit: 'gap scale step',
  },
  {
    id: 'C4',
    label: 'Control scale',
    meaning: 'Interactive control family height/pad (md≈40, lg≈48) with touch clamps.',
    mustNot: [
      'Allow hit target below 44×44 CSS px',
      'Allow mobile primary CTA height below 48 CSS px',
    ],
    scope: 'Customer Button adapter -> --vcl-control-height-md/lg, pad-x',
    promotionTarget: 'rpapp-customer/src/shared/ui/Button.tsx',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C4,
    stepUnit: 'control height step (px before clamp)',
  },
  {
    id: 'C5',
    label: 'Surface padding',
    meaning: 'Card/surface grouping padding hierarchy (not chrome inset).',
    mustNot: [
      'Use ornamental padding without grouping function',
      'Bind to technical safe-area reserves',
    ],
    scope: 'Card/surface -> --vcl-surface-pad-*',
    promotionTarget: 'rpapp-customer/src/styles/customer.extensions.css',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C5,
    stepUnit: 'surface pad step',
  },
  {
    id: 'C6',
    label: 'Control radius',
    meaning: 'Control radius family; live sm = 0.375rem (theme.css alias, no write-through).',
    mustNot: [
      'Round everything',
      'Auto-bind all radii on the surface',
    ],
    scope: '--vcl-control-radius-* aliases customer --radius-* consumers',
    promotionTarget: 'shared/src/tokens/theme.css',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C6,
    stepUnit: 'radius step (±1 ≈ 0.0625rem)',
  },
  {
    id: 'C7',
    label: 'Surface radius',
    meaning: 'Separate surface/card radius family (independent of C6).',
    mustNot: [
      'Auto-bind to C6',
      'Prefer fashion over lowest effective strength',
    ],
    scope: '--vcl-surface-radius-* for Card/surface only',
    promotionTarget: 'rpapp-customer/src/styles/customer.extensions.css',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C7,
    stepUnit: 'surface radius step',
  },
  {
    id: 'C8',
    label: 'Content width',
    meaning:
      'Task-focus max-width by content type (shell/standard/wide/narrow/auth); auth field-stack is a separate role.',
    mustNot: [
      'One mechanical max-width for all screens',
      'Bind auth content-max to CUSTOMER_AUTH_FIELD_CLASS',
    ],
    scope: 'customerTopBarStyles width families -> --vcl-content-max-*',
    promotionTarget: 'rpapp-customer/src/app/customerTopBarStyles.ts',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C8,
    stepUnit: 'content-max step (rem per role, clamped)',
  },
  {
    id: 'C9',
    label: 'Page gutter',
    meaning: 'Responsive page gutter with safe-area + dynamic viewport preservation.',
    mustNot: [
      'Drop safe-area + dynamic viewport invariants',
      'Pack env(safe-area-inset-*) into shared --vcl-page-gutter-x (CSS owns max())',
    ],
    scope: '.customer-safe-area-x -> --vcl-page-gutter-x (token rem; L/R safe-area in CSS)',
    promotionTarget: 'rpapp-customer/src/styles/customer.extensions.css',
    min: -2,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C9,
    stepUnit: 'gutter token step (rem only; CSS max with safe-area)',
  },
  {
    id: 'C10',
    label: 'Surface elevation',
    meaning: 'Hierarchy elevation at lowest effective; soft customer shadows.',
    mustNot: [
      'Decorative shadow without hierarchy function',
      'Swap soft customer --shadow-* for admin elevation',
    ],
    scope: '--vcl-elevation-* -> soft --shadow-card / --shadow-popover',
    promotionTarget: 'rpapp-customer/src/styles/customer.extensions.css',
    min: 0,
    max: 2,
    baseline: VCL_BASELINE_STEPS.C10,
    stepUnit: 'elevation strength enum (0=soft live, 1-2 stronger soft only)',
  },
] as const;

if (VCL_CONTROLS.length !== 10) {
  throw new Error(`VCL_CONTROLS must be exactly 10 (got ${VCL_CONTROLS.length})`);
}

for (let i = 0; i < VCL_CONTROLS.length; i += 1) {
  const control = VCL_CONTROLS[i];
  const expectedId = VCL_CONTROL_IDS[i];
  if (control === undefined || expectedId === undefined || control.id !== expectedId) {
    throw new Error(`VCL_CONTROLS order mismatch at index ${i}`);
  }
}

export function getVclControl(id: VclControlId): VclControlDefinition {
  const found = VCL_CONTROLS.find((c) => c.id === id);
  if (found === undefined) {
    throw new Error(`Unknown VCL control: ${id}`);
  }
  return found;
}
